import "mapbox-gl/dist/mapbox-gl.css";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import Map, {
  AttributionControl,
  Layer,
  MapEvent,
  MapLayerMouseEvent,
  MapStyleDataEvent,
  MapboxGeoJSONFeature,
  Popup,
  Source,
  ViewStateChangeEvent,
} from "react-map-gl";
import { FeatureCollection, Point } from 'geojson';
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  getAreaLayers,
  getDistrictLayers,
  getSectorLayers,
  pointStyle,
} from "./layers";
import { useMapStyle } from "./config";
import { Overlay } from "./InsightMapOverlay";
import { LocationResult } from "@/types";
import { extractBreaks, getFillColor } from "@/utils";

type InsightMapProps = {
  scores: LocationResult;
};

const InsightMap: React.FC<InsightMapProps> = ({ scores }) => {
  const theme = useTheme();
  const mapStyle = useMapStyle(theme.palette.mode);

  const areaLayers = getAreaLayers(theme);
  const districtLayers = getDistrictLayers(theme);
  const sectorLayers = getSectorLayers(theme);

  const [selected, setSelected] = useState<MapboxGeoJSONFeature | undefined>(
    undefined,
  );
  const [hovered, setHovered] = useState<number | string | undefined>(0);
  const [cursor, setCursor] = useState<string>("auto");
  const [breaks, setBreaks] = useState<number[]>([]);
  const [firstTime, setFirstTime] = useState<boolean>(true);

  const [pointData, setPointData] = useState<FeatureCollection<Point> | null>(null);
  const [pointInfo, setPointInfo] = useState<{name: string, latitude: number, longitude: number} | null>(null);

  const onFileUpload = async(event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // 1. Read the file as text
      const text = await file.text();

      // 2. Parse the JSON
      const json = JSON.parse(text);

      // 3. Basic validation: check if it looks like GeoJSON
      if (json.type === 'FeatureCollection' && Array.isArray(json.features)) {
        setPointData(json);
      } else {
        alert("Invalid GeoJSON format. Please upload a FeatureCollection.");
      }
    } catch (error) {
      console.error("Error parsing JSON:", error);
      alert("Failed to read file. Ensure it is a valid .json or .geojson file.");
    }
  }

  const onLoad = useCallback(
    (event: MapEvent) => {
      const map = event.target;
      map &&
        scores.area.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "area-source",
              sourceLayer: "postal_area_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map.setPaintProperty(
          "area-base",
          "fill-color",
          getFillColor(extractBreaks(scores.area), theme.palette.gradient),
        );
      map &&
        scores.district.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "district-source",
              sourceLayer: "postal_district_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map.setPaintProperty(
          "district-base",
          "fill-color",
          getFillColor(extractBreaks(scores.district), theme.palette.gradient),
        );
      map &&
        scores.sector.forEach((value) => {
          map.setFeatureState(
            {
              id: value.gid,
              source: "sector-source",
              sourceLayer: "postal_sector_test",
            },
            { score: value.rate, name: value.name, level: value.level },
          );
        });
      map &&
        map.setPaintProperty(
          "sector-base",
          "fill-color",
          getFillColor(extractBreaks(scores.sector), theme.palette.gradient),
        );
      setBreaks(extractBreaks(scores.area));
    },
    [scores, theme.palette.gradient],
  );

  useEffect(() => {
    setBreaks(extractBreaks(scores.area));
  }, [scores]);

  const onClick = useCallback((event: MapLayerMouseEvent) => {
    const feature = event.features && event.features[0];
    if (feature && feature.state.name) {
      setSelected(feature);
    } else {
      clearSelected();
    }
  }, []);

  const onHover = useCallback((event: MapLayerMouseEvent) => {
    const feature = event.features && event.features[0];
    if (feature) {
      if (feature.layer.id === 'point' && feature['properties']) {
        setHovered(0);
        setPointInfo({
          name: feature.properties['name'],
          latitude: feature.properties['latitude'],
          longitude: feature.properties['longitude']
        });
      } else if (feature.state.name){
        setHovered(feature.id);
      } else {
        setHovered(0);
      }
    } else {
      setHovered(0);
    }
  }, []);

  const onZoom = useCallback(
    (event: ViewStateChangeEvent) => {
      const zoom = event.viewState.zoom;
      if (zoom > 9) {
        setBreaks(extractBreaks(scores.sector));
      } else if (zoom > 7) {
        setBreaks(extractBreaks(scores.district));
      } else {
        setBreaks(extractBreaks(scores.area));
      }
    },
    [scores],
  );

  const onStyleData = useCallback((event: MapStyleDataEvent) => {
    setFirstTime(false);
  }, []);

  const clearSelected = () => {
    setSelected(undefined);
  };

  const onMouseEnter = useCallback(() => setCursor("pointer"), []);
  const onMouseLeave = useCallback(() => setCursor("auto"), []);

  const hoverFilter = useMemo(() => ["==", ["id"], hovered], [hovered]);
  const selectFilter = useMemo(
    () => ["==", ["id"], selected ? selected.id : 0],
    [selected],
  );

  return (
    <Box
      height="100%"
      minHeight="750px"
      width="100%"
      position="relative"
      sx={{
        overflow: "hidden",
        border: 1,
        borderRadius: 1,
        borderColor: theme.palette.divider,
        ".mapbox-improve-map": {
          display: "none",
        },
        ".mapboxgl-ctrl.mapboxgl-ctrl-attrib": {
          backgroundColor: "transparent !important",
        },
        ".mapboxgl-ctrl-attrib-inner": {
          color: theme.palette.text.secondary,
        },
        ".mapboxgl-ctrl-attrib a": {
          color: theme.palette.text.secondary,
        },
        ".mapboxgl-popup-content": {
          backgroundColor: theme.palette.info.main,
          color: theme.palette.info.contrastText,
          padding: "4px",
          borderRadius: "6px",
          textAlign: "center"
        },
        ".mapboxgl-popup-anchor-top .mapboxgl-popup-tip": {
          borderBottomColor: `${theme.palette.info.main} !important`
        },
        ".mapboxgl-popup-anchor-bottom .mapboxgl-popup-tip": {
          borderTopColor: `${theme.palette.info.main} !important`
        },
        ".mapboxgl-popup-anchor-left .mapboxgl-popup-tip": {
          borderRightColor: `${theme.palette.info.main} !important`
        },
        ".mapboxgl-popup-anchor-right .mapboxgl-popup-tip": {
          borderLeftColor: `${theme.palette.info.main} !important`
        }
      }}
    >
      <Map
        initialViewState={{
          longitude: -4.3,
          latitude: 54.8,
          zoom: 4.8,
        }}
        projection={{ name: "mercator" }}
        minZoom={4}
        maxBounds={[
          [-30, 36],
          [30, 66],
        ]}
        mapStyle={mapStyle}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_MAP_TOKEN}
        cursor={cursor}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        onLoad={onLoad}
        onMouseMove={onHover}
        onZoomEnd={onZoom}
        onStyleData={onStyleData}
        interactiveLayerIds={["area-base", "district-base", "sector-base", "point"]}
        attributionControl={false}
        touchPitch={false}
        dragRotate={false}
      >
        <AttributionControl customAttribution=" " />
        <Source
          id="area-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_area_test"
        >
          <Layer beforeId="waterway-label" {...areaLayers.base} />
          <Layer
            beforeId="waterway-label"
            {...areaLayers.hover}
            filter={hoverFilter}
          />
          <Layer
            beforeId="waterway-label"
            {...areaLayers.select}
            filter={selectFilter}
          />
        </Source>
        <Source
          id="district-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_district_test"
        >
          <Layer beforeId="waterway-label" {...districtLayers.base} />
          <Layer
            beforeId="waterway-label"
            {...districtLayers.hover}
            filter={hoverFilter}
          />
          <Layer
            beforeId="waterway-label"
            {...districtLayers.select}
            filter={selectFilter}
          />
        </Source>
        <Source
          id="sector-source"
          type="vector"
          url="mapbox://mpilarczykstarcount.postal_sector_test"
        >
          <Layer beforeId="waterway-label" {...sectorLayers.base} />
          <Layer
            beforeId="waterway-label"
            {...sectorLayers.hover}
            filter={hoverFilter}
          />
          <Layer
            beforeId="waterway-label"
            {...sectorLayers.select}
            filter={selectFilter}
          />
        </Source>
        {pointData && (
          <Source id="point-source" type="geojson" data={pointData}>
            <Layer {...pointStyle} />
          </Source>
        )}
        <Overlay
          scores={scores}
          selected={selected}
          clearSelected={clearSelected}
          breaks={breaks}
          firstTime={firstTime}
          handleFileUpload={onFileUpload}
        />
        {pointInfo && (
          <Popup
            latitude={pointInfo.latitude}
            longitude={pointInfo.longitude}
            closeButton={false}
            onClose={() => setPointInfo(null)}
          >
            <Typography
              variant="body2"
              sx={{ color: theme.palette.info.contrastText }}
            >
              {pointInfo.name}
            </Typography>
          </Popup>
        )}
      </Map>
    </Box>
  );
};

export default InsightMap;
